// =====================================================
// JOURNAL STATS & DISCIPLINE DASHBOARD - PHASE 4
// Institutional Capital Preservation + Counterfactual Proof + TDI
// =====================================================

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getJournalStats } from '@/services/journalService';
import { getCounterfactualStats } from '@/services/counterfactualService';
import { TDIScoreCard } from './TDIScoreCard';
import DisciplinePremiumCard from './DisciplinePremiumCard';
import TDICoachingSection from './TDICoachingSection';
import AuditExportModal from './AuditExportModal';
import { FeatureGate } from '@/components/FeatureGate';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import type { JournalStats as JournalStatsType } from '@/types/journal';
import type { CounterfactualStats } from '@/types/counterfactual';
import MarketTypeStats from './MarketTypeStats';

const { 
  FiTrendingUp, FiTrendingDown, FiTarget, FiActivity, FiAward, 
  FiPieChart, FiBarChart2, FiShield, FiClock, FiAlertTriangle,
  FiCheckCircle, FiXCircle, FiZap, FiEye, FiSlash, FiDollarSign, FiFileText
} = FiIcons;

const JournalStats: React.FC = () => {
  const [stats, setStats] = useState<JournalStatsType | null>(null);
  const [counterfactualStats, setCounterfactualStats] = useState<CounterfactualStats | null>(null);
  const [tdiData, setTdiData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7' | '30' | 'all'>('30');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { flags } = useFeatureFlags();

  useEffect(() => {
    const loadStats = async () => {
      setIsLoading(true);
      try {
        const days = timeRange === 'all' ? undefined : parseInt(timeRange);
        const [journalData, cfData] = await Promise.all([
          getJournalStats(days),
          getCounterfactualStats(days),
        ]);
        setStats(journalData);
        setCounterfactualStats(cfData);
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, [timeRange]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent" />
        <span className="ml-3 text-gray-400">Loading statistics...</span>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">Unable to load statistics</p>
      </div>
    );
  }

  // Discipline score colors
  const getDisciplineColor = (score: number) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 60) return 'text-cyan-400';
    if (score >= 40) return 'text-amber-400';
    return 'text-red-400';
  };

  const getDisciplineGradient = (score: number) => {
    if (score >= 80) return 'from-emerald-500 to-cyan-500';
    if (score >= 60) return 'from-cyan-500 to-blue-500';
    if (score >= 40) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-pink-500';
  };

  // Overtrading level colors
  const getOvertradingColor = (level: string) => {
    switch (level) {
      case 'severe': return 'text-red-400 bg-red-500/20';
      case 'moderate': return 'text-amber-400 bg-amber-500/20';
      case 'mild': return 'text-yellow-400 bg-yellow-500/20';
      default: return 'text-emerald-400 bg-emerald-500/20';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Time Range + Export Button */}
      <div className="flex items-center justify-end flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {/* Export PDF Button - Institutional AI Only */}
          {flags.hasAuditPdf && (
            <button
              onClick={() => setIsExportModalOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 shadow-lg shadow-purple-500/20 transition-all text-sm font-medium"
            >
              <SafeIcon icon={FiFileText} className="w-4 h-4" />
              Export PDF Audit
            </button>
          )}
          {/* Time Range Selector */}
          <div className="flex items-center gap-1 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 rounded-xl p-1 border border-purple-500/30">
            {(['7', '30', 'all'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  timeRange === range
                    ? 'bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 text-cyan-400 shadow-lg shadow-purple-500/20'
                    : 'text-gray-400 hover:text-cyan-400'
                }`}
              >
                {range === 'all' ? 'All Time' : `${range}D`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Audit Export Modal - Only render if user has access */}
      {flags.hasAuditPdf && (
        <AuditExportModal 
          isOpen={isExportModalOpen} 
          onClose={() => setIsExportModalOpen(false)} 
        />
      )}

      {/* Discipline Drift & Overtrading Warning Banner - Institutional AI Only */}
      {flags.hasBehavioralDriftDetection && (stats.overtradingWarning || stats.disciplineDriftLevel !== 'Stable') && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-xl border ${
            stats.disciplineDriftLevel === 'Overtrading' || stats.overtradingLevel === 'severe' 
              ? 'bg-red-500/10 border-red-500/30' 
              : stats.disciplineDriftLevel === 'Drift'
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-yellow-500/10 border-yellow-500/30'
          }`}
        >
          <div className="flex items-start gap-3">
            <SafeIcon 
              icon={FiAlertTriangle} 
              className={`w-5 h-5 mt-0.5 ${
                stats.disciplineDriftLevel === 'Overtrading' ? 'text-red-400' : 
                stats.disciplineDriftLevel === 'Drift' ? 'text-amber-400' : 'text-yellow-400'
              }`} 
            />
            <div className="flex-1">
              <span className={`font-semibold ${
                stats.disciplineDriftLevel === 'Overtrading' ? 'text-red-400' : 
                stats.disciplineDriftLevel === 'Drift' ? 'text-amber-400' : 'text-yellow-400'
              }`}>
                {stats.disciplineDriftLevel === 'Overtrading' 
                  ? '🚨 Critical Overtrading Mode' 
                  : stats.disciplineDriftLevel === 'Drift'
                  ? '⚠️ Discipline Drift Detected'
                  : 'Discipline Degradation Warning'}
              </span>
              <p className="text-sm text-gray-400 mt-1">
                {stats.disciplineDriftLevel === 'Overtrading' 
                  ? 'Your decision quality is degrading. Capital at risk. Consider pausing to review your decision criteria.'
                  : stats.disciplineDriftLevel === 'Drift'
                  ? 'Trading patterns suggest discipline erosion. Review recent decisions for alignment with institutional criteria.'
                  : 'Trade frequency may be too high. Monitor your capital preservation ratio.'}
              </p>
              {/* Drift Indicators */}
              <div className="flex flex-wrap gap-3 mt-3 text-xs">
                {stats.disciplineDriftIndicators?.tradeFrequencyTrend === 'increasing' && (
                  <span className="px-2 py-1 rounded-full bg-red-500/20 text-red-400">
                    Trade frequency ↑
                  </span>
                )}
                {stats.disciplineDriftIndicators?.riskOffViolations > 0 && (
                  <span className="px-2 py-1 rounded-full bg-amber-500/20 text-amber-400">
                    {stats.disciplineDriftIndicators.riskOffViolations} Risk-Off violations
                  </span>
                )}
                {stats.disciplineDriftIndicators?.recentLossStreak > 0 && (
                  <span className="px-2 py-1 rounded-full bg-red-500/20 text-red-400">
                    Loss streak: {stats.disciplineDriftIndicators.recentLossStreak}
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Phase 3: Discipline Status Indicator */}
      <div className="flex items-center justify-between p-4 rounded-xl bg-gray-800/30 border border-white/5">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            stats.disciplineDriftLevel === 'Stable' ? 'bg-emerald-500/20' :
            stats.disciplineDriftLevel === 'Drift' ? 'bg-amber-500/20' : 'bg-red-500/20'
          }`}>
            <SafeIcon 
              icon={stats.disciplineDriftLevel === 'Stable' ? FiCheckCircle : FiAlertTriangle} 
              className={`w-5 h-5 ${
                stats.disciplineDriftLevel === 'Stable' ? 'text-emerald-400' :
                stats.disciplineDriftLevel === 'Drift' ? 'text-amber-400' : 'text-red-400'
              }`}
            />
          </div>
          <div>
            <span className="text-sm font-medium text-white">Discipline Status</span>
            <p className="text-xs text-gray-500">
              {stats.disciplineDriftLevel === 'Stable' 
                ? 'Trading within institutional parameters'
                : stats.disciplineDriftLevel === 'Drift'
                ? 'Patterns suggest discipline erosion'
                : 'Critical - immediate review recommended'}
            </p>
          </div>
        </div>
        <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${
          stats.disciplineDriftLevel === 'Stable' ? 'bg-emerald-500/20 text-emerald-400' :
          stats.disciplineDriftLevel === 'Drift' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {stats.disciplineDriftLevel === 'Stable' ? '✅ Stable' :
           stats.disciplineDriftLevel === 'Drift' ? '⚠️ Drift' : '🚨 Overtrading'}
        </span>
      </div>

      {/* TDI Score Card - Primary MOAT Feature */}
      <FeatureGate feature="hasTdiScore" showUpgradePrompt={true}>
        <TDIScoreCard onScoreCalculated={(data) => setTdiData(data)} />
      </FeatureGate>

      {/* TDI Coaching Section - Below TDI Score */}
      <FeatureGate feature="hasTdiScore" showUpgradePrompt={false}>
        <TDICoachingSection tdiData={tdiData ? {
          tdi_score: tdiData.tdi_score,
          btdi: tdiData.btdi_score,
          cetdi: tdiData.cetdi_score,
          components: {
            behavior: tdiData.components,
            capital: tdiData.cetdi_components
          },
          directional_bias: tdiData.directional_bias_adjustment !== 0 ? {
            has_warning: true,
            dominant_side: tdiData.directional_bias_adjustment < 0 ? 'LONG' : 'SHORT'
          } : undefined,
          stats: {
            decisions_count: tdiData.decisions_counted || stats?.totalAnalyses || 0,
            executed_trades_count: (stats?.wins || 0) + (stats?.losses || 0) + (stats?.breakevens || 0),
            has_pnl_data: (stats?.totalRMultiple ?? 0) !== 0,
            has_counterfactual_data: (counterfactualStats?.completedTracking ?? 0) > 0
          },
          period_days: parseInt(timeRange) || 30
        } : null} />
      </FeatureGate>

      {/* Discipline Premium Card - Impulsive Trader Benchmark */}
      <FeatureGate feature="hasImpulsiveTraderBenchmark" showUpgradePrompt={true}>
        <DisciplinePremiumCard days={timeRange === 'all' ? undefined : parseInt(timeRange)} />
      </FeatureGate>

      {/* Hero Metrics Grid - 3 Key Scores (gated: Pro Trader+ — these are
          Decision Quality / Capital Preservation / Risk Avoidance, sold on Pro Trader card) */}
      <FeatureGate feature="hasDecisionQualityScore" showUpgradePrompt={true}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        
        {/* Decision Quality Score - Primary Hero */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-white/5 relative overflow-hidden"
        >
          <div className={`absolute inset-0 bg-gradient-to-br ${getDisciplineGradient(stats.disciplineScore)} opacity-5`} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <SafeIcon icon={FiShield} className={`w-5 h-5 ${getDisciplineColor(stats.disciplineScore)}`} />
              <span className="text-sm font-medium text-gray-400">Decision Quality Score</span>
            </div>
            <div className="flex items-end gap-4">
              <span className={`text-5xl font-bold ${getDisciplineColor(stats.disciplineScore)}`}>
                {stats.disciplineScore}
              </span>
              <span className="text-gray-500 text-sm mb-2">/100</span>
            </div>
            <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${stats.disciplineScore}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                className={`h-full bg-gradient-to-r ${getDisciplineGradient(stats.disciplineScore)}`}
              />
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Institutional discipline measurement
            </p>
          </div>
        </motion.div>

        {/* Capital Preservation Rate */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-white/5 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-5" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-cyan-400" />
              <span className="text-sm font-medium text-gray-400">Capital Preservation</span>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-5xl font-bold text-cyan-400">
                {stats.capitalPreservationRate.toFixed(1)}
              </span>
              <span className="text-cyan-400 text-lg mb-2">%</span>
            </div>
            <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-400"></span>
                <span>{stats.noTradeDecisions} NO-TRADE</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                <span>{stats.waitDecisions} WAIT</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Protected capital opportunities
            </p>
          </div>
        </motion.div>

        {/* Risk Avoidance Score */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6 rounded-2xl bg-gradient-to-br from-gray-800/80 to-gray-900/80 border border-white/5 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 opacity-5" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <SafeIcon icon={FiZap} className="w-5 h-5 text-purple-400" />
              <span className="text-sm font-medium text-gray-400">Risk Avoidance Score</span>
            </div>
            <div className="flex items-end gap-2">
              <span className={`text-5xl font-bold ${
                stats.riskAvoidanceScore >= 70 ? 'text-emerald-400' :
                stats.riskAvoidanceScore >= 50 ? 'text-purple-400' : 'text-amber-400'
              }`}>
                {stats.riskAvoidanceScore}
              </span>
              <span className="text-gray-500 text-sm mb-2">/100</span>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-gray-500">Regime Compliance</span>
              <span className={`font-semibold ${
                stats.regimeCompliance >= 70 ? 'text-emerald-400' : 
                stats.regimeCompliance >= 50 ? 'text-amber-400' : 'text-red-400'
              }`}>{stats.regimeCompliance}%</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Trading within regime rules
            </p>
          </div>
        </motion.div>
      </div>
      </FeatureGate>

      {/* Phase 4: Counterfactual Proof - Capital Preserved Stats */}
      {counterfactualStats && counterfactualStats.completedTracking > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-xl bg-gradient-to-r from-emerald-500/5 to-cyan-500/5 border border-emerald-500/20"
        >
          <div className="flex items-center gap-2 mb-4">
            <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-emerald-400" />
            <h4 className="text-sm font-semibold text-white">Capital Preserved (Counterfactual Proof)</h4>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <div className="text-xs text-gray-500 mb-1">Total Preserved</div>
              <div className={`text-2xl font-bold ${
                counterfactualStats.totalCapitalPreservedR >= 0 ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                {counterfactualStats.totalCapitalPreservedR >= 0 ? '+' : ''}{counterfactualStats.totalCapitalPreservedR.toFixed(1)}R
              </div>
              <div className="text-xs text-gray-500 mt-1">
                ${Math.abs(counterfactualStats.totalCapitalPreservedUsd).toLocaleString()}
              </div>
            </div>
            
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <div className="text-xs text-gray-500 mb-1">Avg per Decision</div>
              <div className={`text-2xl font-bold ${
                (counterfactualStats.avgCapitalPreservedR || 0) >= 0 ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                {counterfactualStats.avgCapitalPreservedR !== null 
                  ? `${counterfactualStats.avgCapitalPreservedR >= 0 ? '+' : ''}${counterfactualStats.avgCapitalPreservedR.toFixed(1)}R`
                  : '—'}
              </div>
            </div>
            
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <div className="text-xs text-gray-500 mb-1">Capital Saved</div>
              <div className="text-2xl font-bold text-emerald-400">
                {counterfactualStats.decisionsThatSavedCapital}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                of {counterfactualStats.completedTracking} tracked
              </div>
            </div>
            
            <div className="p-4 bg-gray-800/50 rounded-xl">
              <div className="text-xs text-gray-500 mb-1">Missed Opps</div>
              <div className="text-2xl font-bold text-amber-400">
                {counterfactualStats.decisionsThatMissedOpportunity}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Still protected capital
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
            <p className="text-xs text-gray-400">
              <span className="text-emerald-400 font-semibold">Counterfactual Proof:</span> This shows what would have 
              happened if you traded when AI said NO_TRADE or WAIT. Positive R = capital preserved, 
              Negative R = opportunity cost (still the right discipline choice).
            </p>
          </div>
        </motion.div>
      )}

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard
          icon={FiActivity}
          label="Total Analyses"
          value={stats.totalAnalyses.toString()}
          color="text-purple-400"
        />
        <StatCard
          icon={FiTrendingUp}
          label="TRADE Signals"
          value={stats.tradeDecisions.toString()}
          subValue={`${stats.totalAnalyses > 0 ? ((stats.tradeDecisions / stats.totalAnalyses) * 100).toFixed(0) : 0}%`}
          color="text-emerald-400"
        />
        <StatCard
          icon={FiTarget}
          label="Win Rate"
          value={stats.winRate !== null ? `${stats.winRate.toFixed(1)}%` : '—'}
          subValue={`${stats.wins}W / ${stats.losses}L`}
          color="text-cyan-400"
        />
        <StatCard
          icon={FiDollarSign}
          label="Net P&L"
          value={stats.totalRMultiple !== null ? `${stats.totalRMultiple >= 0 ? '+' : ''}$${Math.abs(stats.totalRMultiple).toLocaleString()}` : '—'}
          color={stats.totalRMultiple && stats.totalRMultiple > 0 ? 'text-emerald-400' : stats.totalRMultiple && stats.totalRMultiple < 0 ? 'text-red-400' : 'text-gray-400'}
        />
        <StatCard
          icon={FiClock}
          label="Pending"
          value={stats.pending.toString()}
          color="text-amber-400"
        />
        <div className={`p-4 rounded-xl border ${getOvertradingColor(stats.overtradingLevel)}`}>
          <div className="flex items-center gap-2 mb-1">
            <SafeIcon 
              icon={stats.overtradingLevel === 'none' ? FiCheckCircle : FiAlertTriangle} 
              className="w-4 h-4" 
            />
            <span className="text-xs text-gray-500 truncate">Overtrading</span>
          </div>
          <div className="text-xl font-bold capitalize">
            {stats.overtradingLevel === 'none' ? 'Clear' : stats.overtradingLevel}
          </div>
        </div>
      </div>

      {/* Breakdown Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* By Market Type */}
        <div className="p-5 rounded-xl bg-gray-800/50 border border-white/5">
          <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <SafeIcon icon={FiBarChart2} className="w-4 h-4 text-cyan-400" />
            By Market Type
          </h4>
          <div className="space-y-3">
            {Object.entries(stats.byMarket || {}).length > 0 ? (
              Object.entries(stats.byMarket).map(([market, count]) => {
                const percentage = stats.totalAnalyses > 0 ? (count / stats.totalAnalyses) * 100 : 0;
                return (
                  <div key={market}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-400 capitalize">{market}</span>
                      <span className="text-white font-medium">{count}</span>
                    </div>
                    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500"
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-sm">No data yet</p>
            )}
          </div>
        </div>

        {/* Top Assets */}
        <div className="p-5 rounded-xl bg-gray-800/50 border border-white/5">
          <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <SafeIcon icon={FiTarget} className="w-4 h-4 text-emerald-400" />
            Top Assets
          </h4>
          <div className="space-y-3">
            {Object.entries(stats.byAsset || {}).length > 0 ? (
              Object.entries(stats.byAsset)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([asset, count]) => {
                  const percentage = stats.totalAnalyses > 0 ? (count / stats.totalAnalyses) * 100 : 0;
                  return (
                    <div key={asset}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-400">{asset}</span>
                        <span className="text-white font-medium">{count}</span>
                      </div>
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                        />
                      </div>
                    </div>
                  );
                })
            ) : (
              <p className="text-gray-500 text-sm">No data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Outcome Distribution */}
      <div className="p-5 rounded-xl bg-gray-800/50 border border-white/5">
        <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <SafeIcon icon={FiAward} className="w-4 h-4 text-emerald-400" />
          Outcome Distribution
        </h4>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
          <OutcomeCard label="Wins" value={stats.wins} color="bg-emerald-500" />
          <OutcomeCard label="Losses" value={stats.losses} color="bg-red-500" />
          <OutcomeCard label="Breakeven" value={stats.breakevens} color="bg-blue-500" />
          <OutcomeCard label="Missed" value={stats.missed} color="bg-purple-500" />
          <OutcomeCard label="Cancelled" value={stats.cancelled} color="bg-gray-500" />
          <OutcomeCard label="Skipped" value={stats.noTradeOutcomes} color="bg-slate-500" />
          <OutcomeCard label="Awaiting" value={stats.waitOutcomes} color="bg-cyan-500" />
          <OutcomeCard label="Pending" value={stats.pending} color="bg-amber-500" />
        </div>
      </div>

      {/* Institutional Philosophy Banner */}
      <div className="p-5 rounded-xl bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 shadow-lg shadow-purple-500/20">
            <SafeIcon icon={FiShield} className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white mb-1">Risk Committee Philosophy</h4>
            <p className="text-xs text-gray-400 leading-relaxed">
              "Anyone can take a trade. Professionals know when not to."
              <br />
              Snap Chart AI is not built to trade more. It is built to trade less, but better, 
              with full institutional accountability. NO-TRADE and WAIT decisions are valued 
              outcomes that demonstrate disciplined capital preservation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper components
const StatCard: React.FC<{
  icon: any;
  label: string;
  value: string;
  subValue?: string;
  color: string;
}> = ({ icon, label, value, subValue, color }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="p-4 rounded-xl bg-gray-800/30 border border-white/5"
  >
    <div className="flex items-center gap-2 mb-1">
      <SafeIcon icon={icon} className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-gray-500 truncate">{label}</span>
    </div>
    <div className="text-xl font-bold text-white">{value}</div>
    {subValue && <div className="text-xs text-gray-500">{subValue}</div>}
  </motion.div>
);

const OutcomeCard: React.FC<{
  label: string;
  value: number;
  color: string;
}> = ({ label, value, color }) => (
  <div className="text-center p-3 rounded-lg bg-gray-900/50">
    <div className={`w-3 h-3 rounded-full ${color} mx-auto mb-2`} />
    <div className="text-lg font-bold text-white">{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

export default JournalStats;